import React, { FC, useContext, useEffect, useState } from 'react';
import MonitoringNoContent from '../../../assets/images/monitoring_no_content.png';
import { Box, Button, Typography, useTheme } from '@mui/material';

import { Add } from '@mui/icons-material';
import Section from '../../../components/Main/Section/Section';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../providers/AuthProvider';
import AnalysisReport from '../../../components/Analysis/AnalysisReport';
import ActionBar from '../../../components/ActionBar/ActionBar';
import styled from 'styled-components';
import ContentContainer from '../../../components/Containers/ContentContainer';
import LeftContainer from '../../../components/Containers/LeftContainer';
import ReviewAiService from '../../../services/ReviewAiService';
import { useNavigate } from 'react-router-dom';
import Helper from '../../../shared/helpers/Helper';
import Accordion from '../../../components/Accordion/Accordion';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';

export interface IAnalysisReport {
  attempts: number;
  title: string;
  company: string;
  completedAt: string | null;
  createdAt: string;
  deletedAt: string | null;
  domainId: string;
  error: boolean;
  id: string;
  monitoringId: string;
  platform: string;
  rating: number;
  reviewCount: number;
  analyzedCount: number;
  updatedAt: string;
  result: string;
  dateFrom?: string;
  dateTo?: string;
}

const AddEditContainer = styled.div`
  margin-left: auto;
`;

const ANALYSIS_ROLE_NAME = 'can-use-aireviews';
const ANALYSIS_LIMIT_NAME = 'limitaireviews';

const Analysis: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole, exceedsLimit, getLimit } = useContext(AuthContext);

  const [generatedReports, setGeneratedReports] = useState<IAnalysisReport[]>([]);
  const [monthlyReports, setMonthlyReports] = useState<number>(0);

  const [loading, setLoading] = useState<boolean>(true);

  const limit = getLimit(ANALYSIS_LIMIT_NAME, 50);

  const navigate = useNavigate();

  const { setCurrentView } = useContext(CurrentViewContext);

  useEffect(() => {
    setCurrentView(t('nav_analysis'));
    document.title = `${t('app_branding')} ${t('nav_analysis')}`;
  }, [setCurrentView, t]);

  const isCreatedThisMonth = (createdAt: string) => {
    const createdDate = new Date(createdAt);
    const now = new Date();

    const thisMonth =
      createdDate.getFullYear() === now.getFullYear() && createdDate.getMonth() === now.getMonth();
    return thisMonth;
  };

  const loadData = async () => {
    setLoading(true);
    setMonthlyReports(0);
    let reports = await ReviewAiService.listAll();
    reports = reports.filter((report: any) => report.deletedAt === null);
    setGeneratedReports(reports);
    let monthlyCount = 0;
    for (const report of reports) {
      if (isCreatedThisMonth(report.createdAt)) {
        monthlyCount += 1;
      }
    }
    setMonthlyReports(monthlyCount);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  function groupByMonth(generatedReports: IAnalysisReport[]): { [key: string]: IAnalysisReport[] } {
    const monthlyReports: ReturnType<typeof groupByMonth> = {};
    for (const report of generatedReports) {
      const monthString = Helper.getCustomFormattedDate(report.createdAt, 'MMMM yyyy');
      if (!monthlyReports[monthString]) {
        monthlyReports[monthString] = [];
      }
      monthlyReports[monthString].push(report);
    }

    return monthlyReports;
  }

  return (
    <Section>
      {generatedReports.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            borderRadius: `${theme.shape.borderRadius}px`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <img
            width={300}
            src={MonitoringNoContent}
            alt="No Reports"
          />
          <Typography sx={{ mt: theme.spacing(4) }}>{t('no_records')}</Typography>
          {hasRole(ANALYSIS_ROLE_NAME) && !exceedsLimit(ANALYSIS_LIMIT_NAME, 1) && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('create')}
              sx={{ mt: theme.spacing(2) }}
            >
              {t('new_analysis')}
            </Button>
          )}
        </Box>
      ) : (
        <>
          <ContentContainer sx={{ marginBottom: '1rem' }}>
            <LeftContainer>
              <ActionBar
                children={
                  <AddEditContainer>
                    {hasRole(ANALYSIS_ROLE_NAME) &&
                      !exceedsLimit(ANALYSIS_LIMIT_NAME, monthlyReports + 1) && (
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => navigate('create')}
                        >
                          {t('new_analysis')}
                        </Button>
                      )}
                  </AddEditContainer>
                }
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: theme.spacing(1),
                  marginTop: theme.spacing(3),
                }}
              >
                <Typography sx={{ fontSize: '12px' }}>
                  {t('num_analysis', { num: generatedReports.length })}
                </Typography>
                <Typography sx={{ fontSize: '12px' }}>
                  {t('monthly_analysis_limit', { remaining: limit - monthlyReports, limit: limit })}
                </Typography>
              </Box>
              {!loading &&
                Object.entries(groupByMonth(generatedReports)).map(([month, reports], idx) => (
                  <>
                    <Accordion
                      expanded={idx === 0}
                      selfManageExpanded={true}
                      onExpandRequest={() => {}}
                      summary={
                        <Typography
                          fontSize={'1.5rem'}
                          color={theme.palette.primary.main}
                        >
                          {month}
                        </Typography>
                      }
                      sx={{
                        boxShadow: 'none',
                        background: 'transparent',
                        '& .MuiAccordionDetails-root': { padding: 0 },
                      }}
                      summarySx={{
                        background: 'white',
                        display: 'flex',
                        borderRadius: '4px',
                        boxShadow:
                          'rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px',
                      }}
                      details={
                        <Section>
                          {reports.map((report) => (
                            <AnalysisReport
                              report={report}
                              onDelete={() => loadData()}
                            />
                          ))}
                        </Section>
                      }
                    />
                  </>
                ))}
            </LeftContainer>
          </ContentContainer>
        </>
      )}
    </Section>
  );
};

export default Analysis;
